import { useState, type JSX } from 'react';

import { FAQ } from './content.js';
import { Band } from './sections.js';

/**
 * FAQ accordion.
 *
 * Native `<details>`/`<summary>` would be shorter, but the rows need a controlled +/− indicator and
 * the marketing design shows one row open at a time; a button with `aria-expanded` controlling a
 * region is the accessible equivalent and keeps keyboard behaviour for free.
 */
export function Faq(): JSX.Element {
  const [open, setOpen] = useState<number | null>(null);

  return (
    <Band>
      <div className="nrx-card mx-auto flex max-w-[52rem] flex-col gap-2 rounded-[var(--radius-lg)] px-6 py-10 sm:px-10">
        <h2 className="mb-4 text-center text-2xl font-bold text-[var(--forest)]">
          Frequently Asked Questions
        </h2>
        {FAQ.map((item, i) => {
          const isOpen = open === i;
          const panelId = `faq-panel-${String(i)}`;
          return (
            <div
              key={item.question}
              className="border-b border-[var(--color-card-border)] last:border-0"
            >
              {/*
                Raw <button> rather than @nrx/ui's Button: `.btn` is UNLAYERED in theme.css, so it
                beats Tailwind's layered utilities and forces justify-content:center plus its own
                padding and font-size. An accordion row is a full-width left-aligned target, so
                using Button here means fighting it with four !important overrides for no gain.
              */}
              {/* eslint-disable-next-line no-restricted-syntax -- see comment above */}
              <button
                type="button"
                onClick={() => setOpen(isOpen ? null : i)}
                aria-expanded={isOpen}
                aria-controls={panelId}
                className="flex w-full items-start gap-3 py-4 text-left"
              >
                <span
                  className="mt-0.5 shrink-0 text-lg font-bold leading-none text-[var(--coral)]"
                  aria-hidden="true"
                >
                  {isOpen ? '−' : '+'}
                </span>
                <span className="font-semibold text-[var(--forest)]">{item.question}</span>
              </button>
              <div id={panelId} hidden={!isOpen} className="pb-4 pl-8">
                {item.answer !== '' && <p className="nrx-prose text-[var(--ink)]">{item.answer}</p>}
                {item.bullets !== undefined && (
                  <ul className="ml-4 list-disc nrx-prose text-[var(--ink)]">
                    {item.bullets.map((b) => (
                      <li key={b}>{b}</li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </Band>
  );
}
